import { useState, useEffect, useCallback, useRef } from "react";
import { axiosInstance } from "../axios";

const isPrerendering = typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap');

export default function useAppSetup() {
  const [jobCategories, setJobCategories] = useState([]);
  const [subscriptionPackages, setSubscriptionPackages] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [isDataLoading, setIsDataLoading] = useState(!isPrerendering);
  const [otherEndpointsFailed, setOtherEndpointsFailed] = useState(false);
  const [isReviewsLoading, setIsReviewsLoading] = useState(!isPrerendering);
  const [reviewsError, setReviewsError] = useState(false);
  const mountedRef = useRef(false);
  const reviewRequestRef = useRef(null);
  const isApiDown = otherEndpointsFailed && reviewsError && !isReviewsLoading;

  const retryReviews = useCallback(async () => {
    if (!mountedRef.current || reviewRequestRef.current) return { ok: false };
    const controller = new AbortController();
    reviewRequestRef.current = controller;
    setIsReviewsLoading(true);
    try {
      const response = await axiosInstance.get("reviews/approved/", {
        signal: controller.signal,
      });
      if (controller.signal.aborted) return { ok: false };
      if (!Array.isArray(response.data)) throw new Error("Invalid reviews response");
      setReviews(response.data);
      setReviewsError(false);
      return { ok: true };
    } catch (error) {
      if (!controller.signal.aborted) setReviewsError(true);
      return { ok: false };
    } finally {
      if (reviewRequestRef.current === controller) {
        reviewRequestRef.current = null;
        if (!controller.signal.aborted) setIsReviewsLoading(false);
      }
    }
  }, []);

  // These endpoints are public and none of them feed above-the-fold content,
  // so they must never gate first paint (the old splash screen serialized
  // them behind the auth refresh and added ~5s+ to mobile LCP). Fire all
  // three immediately and in parallel; consumers handle their own loading
  // states via isDataLoading.
  useEffect(() => {
    // Skip API calls entirely during react-snap pre-rendering
    if (isPrerendering) return;
    mountedRef.current = true;
    let active = true;

    async function fetchData() {
      setIsDataLoading(true);
      const reviewsRequest = retryReviews();
      const [categories, packages] = await Promise.allSettled([
        axiosInstance.get("jobs/categories/"),
        axiosInstance.get("subscriptions/"),
      ]);
      const approvedReviews = await reviewsRequest;
      if (!active) return;

      if (categories.status === "fulfilled") {
        setJobCategories(categories.value.data);
      }
      if (packages.status === "fulfilled") {
        setSubscriptionPackages(packages.value.data);
      }
      // Only declare the API down when every endpoint failed — one flaky
      // endpoint shouldn't put the whole site into Downtime.
      const bothFailed = [categories, packages].every(
        (r) => r.status === "rejected"
      );
      setOtherEndpointsFailed(bothFailed);
      if (bothFailed && !approvedReviews.ok) {
        console.error("Failed to fetch initial app data:", categories.reason);
      }

      setIsDataLoading(false);
    }

    fetchData();
    return () => {
      active = false;
      mountedRef.current = false;
      reviewRequestRef.current?.abort();
      reviewRequestRef.current = null;
    };
  }, [retryReviews]);

  return {
    jobCategories,
    subscriptionPackages,
    reviews,
    isApiDown,
    isDataLoading,
    isReviewsLoading,
    reviewsError,
    retryReviews,
  };
}
